/**
 * Send a finished episode to the collection endpoint.
 *
 * The whole point of the simulator is to produce driving data, and data that
 * only ever reaches the player's Downloads folder is data nobody has. Every
 * completed attempt is posted to `/api/runs` — same origin, so no CORS and no
 * third party in the path.
 *
 * Three rules this module exists to keep:
 *
 * 1. **It never blocks or breaks play.** A failed upload is logged and
 *    forgotten. A player on a bad connection still finishes their run, still
 *    sees their report, still gets the Download JSON button. Losing one
 *    episode is a far smaller cost than a dialog in front of someone driving.
 * 2. **It never sends the same episode twice.** Retry, replay and the
 *    end-panel buttons all re-enter the same code path; `episode_id` is a
 *    per-attempt uuid, and one that has been accepted is remembered for the
 *    session.
 * 3. **It refuses to send something absurd.** A malformed or enormous body is
 *    dropped here rather than at the server, where it would cost bandwidth on
 *    a phone connection first.
 */

import type { EpisodeData } from "./schema";

/** Endpoint, same-origin. nginx proxies /api/ to the collector. */
const ENDPOINT = `${import.meta.env.BASE_URL}api/runs`;

/**
 * Largest body worth sending (bytes).
 *
 * A 60 s attempt at 20 Hz is ~1200 samples and lands near 300 KB. 4 MB is
 * generous room for a long map, and small enough that a runaway episode
 * cannot sit on a phone's uplink for a minute.
 */
const MAX_BYTES = 4 * 1024 * 1024;

/** Episode ids already accepted this session. */
const sent = new Set<string>();

export interface UploadResult {
  ok: boolean;
  /** Why it did not go, for the console. Never shown to a player. */
  reason?: string;
}

export async function uploadEpisode(ep: EpisodeData): Promise<UploadResult> {
  if (!ep?.episode_id) return { ok: false, reason: "no episode_id" };
  if (sent.has(ep.episode_id)) return { ok: false, reason: "already sent" };

  let body: string;
  try {
    body = JSON.stringify(ep);
  } catch (e) {
    return { ok: false, reason: `not serialisable: ${String(e)}` };
  }
  if (body.length > MAX_BYTES) {
    return { ok: false, reason: `${body.length} bytes exceeds the ${MAX_BYTES} cap` };
  }

  // Claim the id before awaiting: two finishes in the same tick (retry pressed
  // as the run ends) would otherwise both pass the check above and send twice.
  sent.add(ep.episode_id);
  try {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "omit",
      // NOT `keepalive: true`. It looks right — "let the upload finish even if
      // the page goes away" — but a keepalive fetch body is capped at 64 KiB
      // by the Fetch spec, and a real episode is ~130 KB. The request then
      // fails as a bare "TypeError: Failed to fetch" before it ever leaves the
      // browser, with nothing in the server log to show for it. The upload
      // runs while the end panel is on screen, so the page is still there.
    });
    if (!res.ok) {
      sent.delete(ep.episode_id);
      return { ok: false, reason: `HTTP ${res.status}` };
    }
    return { ok: true };
  } catch (e) {
    sent.delete(ep.episode_id);
    return { ok: false, reason: String(e) };
  }
}

/** Whether an episode has been accepted this session (for the HUD's note). */
export function wasUploaded(episodeId: string): boolean {
  return sent.has(episodeId);
}
